import {
  CheckableAvatarSelectItem,
  DropdownSelect,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { searchApi } from 'apis/search';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFilterDropdownItem } from '../../conversationsFiltersToolbar.types';
import { useConversationsPaginatedFilterSearch } from '../../utils/useConversationsPaginatedFilterSearch/useConversationsPaginatedFilterSearch';

/** Represents the component for filtering the conversations by contact. */
export const ConversationsContactFilter = (): JSX.Element => {
  const {
    params: { requester: requesterParam },
    unsetParams,
    patchParams,
  } = useAppSearchParams('/conversations');

  const {
    arg,
    items: contacts,
    hasNextPage,
    changeArg,
    nextPage,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: {
      sort: {
        key: 'display_name',
      },
      filter: {
        facets: {
          user_type: ['contact'],
        },
      },
    },
  });

  const dropdownItems = useMemo<readonly ConversationsFilterDropdownItem[]>(
    () =>
      contacts.map(({ id, display_name, email, phone_number }) => ({
        id,
        name: display_name || email || phone_number || id,
      })),
    [contacts],
  );

  const selectedDropdownItemIds = useMemo(
    () =>
      typeof requesterParam !== 'undefined'
        ? typeof requesterParam === 'string'
          ? [requesterParam]
          : requesterParam
        : [],
    [requesterParam],
  );

  const { handleSearch, handleToggle } = useConversationsPaginatedFilterSearch({
    arg,
    changeArg,
  });

  const handleChange = useCallback(
    (item: ConversationsFilterDropdownItem, checked: boolean) => {
      patchParams({
        set: {
          requester: checked
            ? [...selectedDropdownItemIds, item.id]
            : selectedDropdownItemIds.filter((id) => id !== item.id),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: 'requester',
        value: 'id',
        checked,
      });
    },
    [patchParams, selectedDropdownItemIds],
  );

  const handleClear = useCallback(() => {
    unsetParams(['requester', 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: 'requester' });
  }, [unsetParams]);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={dropdownItems as ConversationsFilterDropdownItem[]}
      selectedItems={
        selectedDropdownItemIds as ConversationsFilterDropdownItem['id'][]
      }
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(nextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Contacts
    </DropdownSelect>
  );
};
